import type {
  FetchSchoolListParams,
  School,
  SchoolListResponse
} from 'Services/SchoolManagement/Features/School/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum SchoolTagTypes {
  SCHOOL = 'School'
}

export const schoolApi = createBaseApi({
  reducerPath: 'schoolApi',
  keepUnusedDataFor: 5,
  tagTypes: [SchoolTagTypes.SCHOOL]
}).injectEndpoints({
  endpoints: builder => ({
    getSchools: builder.query<SchoolListResponse, FetchSchoolListParams | undefined>({
      query: params => ({
        url: '/api/v1/school/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(({ id }) => ({ type: SchoolTagTypes.SCHOOL, id }) as const),
              { type: SchoolTagTypes.SCHOOL, id: RTKCacheTags.LIST }
            ]
          : [{ type: SchoolTagTypes.SCHOOL, id: RTKCacheTags.LIST }]
    }),
    getSingleSchool: builder.query<School, string>({
      query: id => `/api/v1/school/${id}/`,
      providesTags: (_result, _error, id) => [{ type: SchoolTagTypes.SCHOOL, id }]
    }),
    createSingleSchool: builder.mutation<School, Partial<School>>({
      query: data => ({
        url: `/api/v1/school/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: SchoolTagTypes.SCHOOL, id: RTKCacheTags.LIST }]
    }),
    updateSingleSchool: builder.mutation<School, { id: string } & Partial<School>>({
      query: ({ id, ...data }) => ({
        url: `/api/v1/school/${id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: SchoolTagTypes.SCHOOL, id }]
    }),
    deleteSingleSchool: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/school/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: SchoolTagTypes.SCHOOL, id: RTKCacheTags.LIST }]
    })
  })
})

export const {
  useGetSchoolsQuery,
  useGetSingleSchoolQuery,
  useCreateSingleSchoolMutation,
  useUpdateSingleSchoolMutation,
  useDeleteSingleSchoolMutation
} = schoolApi
